import { _ } from "../../../../core/polyglot/polyglot.js";
import { editIcon } from "../../../../core/elements/icons/icons.js";
import { getResultWithHtml } from "../../../../core/helpers/functional-util.js";
import {
	infoCircleIcon,
	snowflakeIcon,
} from "../../../../core/elements/icons/index.js";
import { Widget } from "@foretagsplatsen/widgetjs";
import ForecastConfiguration from "../../../../models/forecast/template/ForecastConfiguration.js";
import MonthPickerWidget from "../../../../core/widgets/import/MonthPickerWidget/MonthPickerWidget.js";
import TabWidget from "../../../../core/widgets/TabWidget/TabWidget.js";
import TextRepresentationAccountSpecificationVisitor from "../../../../models/accountSpecification/visitors/TextRepresentationAccountSpecificationVisitor.js";
import Timespan from "../../../../models/period/Timespan.js";

/**
 * Widget displaying two tabs with the forecast settings
 *
 * @param {ForecastConfiguration} [configuration] - Model containing the
 *   current values of the settings
 * @param {Month[]} allPeriods - All the months within the fiscal
 *   years of the company. This is used to choose a period to freeze
 */
export default class ForecastSettingsWidget extends Widget {
	constructor({
		configuration = new ForecastConfiguration(),
		period,
		allPeriods,
		onPeriodChange = () => {},
	} = {}) {
		super(...arguments);

		this._configuration = configuration;
		this._period = period;
		this._allPeriods = allPeriods;
		this._onPeriodChange = onPeriodChange;
		this._tabsWidget = this._makeTabsWidget();
	}

	renderContentOn(html) {
		html.div({ class: "ForecastSettingsWidget" }, this._tabsWidget);
	}

	_makeTabsWidget() {
		return new TabWidget({
			subTabs: true,
			tabs: [
				{
					name: _("General Settings"),
					id: "general-settings",
					dataTestName: "general-settings",
					content: this._makeGeneralSettingsContent(),
				},
				{
					name: _("VAT Settings"),
					id: "VAT-settings",
					dataTestName: "VAT-settings",
					content: this._makeVATSettingsContent(),
				},
			],
		});
	}

	_makeGeneralSettingsContent() {
		const textRepresentationAccountSpecificationVisitor =
			new TextRepresentationAccountSpecificationVisitor();

		const generalSettingsContent = [
			this._settingRenderer({
				name: _("Bank account"),
				value: this._configuration.getBankAccount(),
			}),
			this._settingRenderer({
				name: _("Existing accounts payable group"),
				value: this._specificationRenderer(
					this._configuration.getExistingAccountsPayable(),
				),
			}),
			this._settingRenderer({
				name: _("Existing accounts payable delays"),
				value: this._makeStringFromDelays(
					this._configuration.getExistingAccountsPayableDelays(),
				),
			}),
			this._settingRenderer({
				name: _("Existing accounts receivable group"),
				value: this._specificationRenderer(
					this._configuration.getExistingAccountsReceivable(),
				),
			}),
			this._settingRenderer({
				name: _("Existing accounts receivable delays"),
				value: this._makeStringFromDelays(
					this._configuration.getExistingAccountsReceivableDelays(),
				),
			}),
			this._settingRenderer({
				name: _("Forecasted accounts payable"),
				value: textRepresentationAccountSpecificationVisitor.visit(
					this._configuration.getForecastAccountsPayable(),
				),
			}),
			this._settingRenderer({
				name: _("Forecasted accounts payable delays"),
				value: this._makeStringFromDelays(
					this._configuration.getForecastAccountsPayableDelays(),
				),
			}),
			this._settingRenderer({
				name: _("Forecasted accounts receivable"),
				value: textRepresentationAccountSpecificationVisitor.visit(
					this._configuration.getForecastAccountsReceivable(),
				),
			}),
			this._settingRenderer({
				name: _("Forecasted accounts receivable delays"),
				value: this._makeStringFromDelays(
					this._configuration.getForecastAccountsReceivableDelays(),
				),
			}),
			this._settingRenderer({
				name: _("Number of periods"),
				value: this._getForecastHorizonAccessor(),
			}),
		];

		if (this._isPeriodSettingAvailable())
			generalSettingsContent.push(this._makePeriodSettingRenderer());

		return generalSettingsContent;
	}

	_isPeriodSettingAvailable() {
		return !!this._period;
	}

	_getFrozenPeriod() {
		return this._configuration.getFrozenCurrentPeriod();
	}

	_isPeriodFrozen() {
		return !!this._getFrozenPeriod();
	}

	_makePeriodSettingRenderer() {
		return this._settingRenderer({
			name: this._getPeriodSettingName(),
			value: getResultWithHtml(this._makeLastActivePeriodRenderer()),
		});
	}

	_getPeriodSettingName() {
		return this._isPeriodFrozen()
			? _("Frozen last active month")
			: _("Last active period");
	}

	_makeLastActivePeriodRenderer() {
		return (html) => {
			if (!this._isEditingPeriod) {
				return [
					this._renderPeriodDisplay(html),
					" ",
					this._renderPeriodInfoIcon(html),
					" ",
					this._renderEditButton(html),
					...this._renderUnfreezeButton(html),
				];
			}
			return this._makeInlinePeriodEditor(html);
		};
	}

	_renderPeriodDisplay(html) {
		return html.span(
			{ "data-test": "last-active-period" },
			this._isPeriodFrozen()
				? this._getFrozenPeriod().toSmartString()
				: this._period?.toSmartString(),
		);
	}

	_renderPeriodInfoIcon(html) {
		return html.span(
			{
				"data-help": "last-active-period",
				title: _(
					"Setting a custom period will freeze it for forecasting calculations",
				),
			},
			infoCircleIcon({
				class: "help-button",
			}),
		);
	}

	_renderEditButton(html) {
		return html.a(
			{
				klass: "btn btn-link",
				"data-test": "edit",
				click: () => this._startEditingPeriod(),
			},
			editIcon({ size: "medium" }),
		);
	}

	_renderUnfreezeButton(html) {
		return this._isPeriodFrozen()
			? [
					" ",
					html.button(
						{
							klass: "btn btn-sm btn-outline-primary",
							"data-test": "unfreeze",
							click: () => this._useSystemPeriod(),
							title: _("Switch back to system period"),
							style: "margin-left: 8px;",
						},
						[
							html.span(
								{ style: "margin-right: 4px;" },
								snowflakeIcon,
							),
							_("Unfreeze"),
						],
					),
				]
			: [];
	}

	_startEditingPeriod() {
		this._isEditingPeriod = true;
		this.update();
	}

	_makeInlinePeriodEditor(html) {
		if (!this._monthPicker) {
			this._monthPicker = new MonthPickerWidget();
			this._configureMonthPickerWidget();
		}

		return html.div(
			{ class: "inline-period-editor" },
			this._monthPicker,
			" ",
			html.button(
				{
					klass: "btn btn-sm btn-warning",
					click: () => this._savePeriod(),
				},
				_("Freeze Period"),
			),
			" ",
			html.button(
				{
					klass: "btn btn-sm btn-default",
					click: () => this._cancelEdit(),
				},
				_("Cancel"),
			),
		);
	}

	_configureMonthPickerWidget() {
		const currentDate = this._period.getStart();

		const firstAvailableDate = new Date(
			currentDate.getFullYear(),
			currentDate.getMonth() -
				this._configuration.getNumberOfReferencePeriods(),
			1,
		);

		const start = this._allPeriods
			.find((month) => month.getStart() >= firstAvailableDate)
			.getStart();

		const end = this._allPeriods.at(-1).getStart();

		this._monthPicker.setRange(new Timespan({ start, end }));
		this._monthPicker.setSelectedDate(currentDate);

		this._monthPicker.onDateChanged.register((date) => {
			this._selectedDate = date;
		});
	}

	_savePeriod() {
		const selectedValue =
			this._selectedDate || this._monthPicker.getSelectedDate();
		const selectedMonth = this._allPeriods.find((month) =>
			month.includes(selectedValue),
		);

		this._onPeriodChange(selectedMonth);
		this._isEditingPeriod = false;
		this._monthPicker = null;
		this.update();
	}

	_useSystemPeriod() {
		this._isEditingPeriod = false;
		this._monthPicker = null;
		this._onPeriodChange(null);
		this.update();
	}

	_cancelEdit() {
		this._isEditingPeriod = false;
		this._monthPicker = null;
		this.update();
	}

	_makeStringFromDelays(delays) {
		return delays.map((delay) => ` (${delay}%)`).toString();
	}

	_makeVATSettingsContent() {
		return [
			this._settingRenderer({
				name: _("Main input VAT account"),
				value: this._configuration.getIncomingVat(),
			}),
			this._settingRenderer({
				name: _("Main output VAT account"),
				value: this._configuration.getOutgoingVat(),
			}),
			this._settingRenderer({
				name: _("Main net VAT account"),
				value: this._configuration.getVatSumAccount(),
			}),
			this._settingRenderer({
				name: _("Existing input VAT group"),
				value: this._specificationRenderer(
					this._configuration.getIncomingVatGroup(),
				),
			}),
			this._settingRenderer({
				name: _("Existing output VAT group"),
				value: this._specificationRenderer(
					this._configuration.getOutgoingVatGroup(),
				),
			}),
			this._settingRenderer({
				name: _("Existing net VAT accounts"),
				value: this._specificationRenderer(
					this._configuration.getSumVatGroup(),
				),
			}),
			this._settingRenderer({
				name: _("VAT payment"),
				value: _("{vatPaymentPeriod}, {vatPaymentLatency} days", {
					vatPaymentPeriod: this._configuration
						.getVatPaymentModel()
						.getVatPaymentPeriodName(),
					vatPaymentLatency: this._configuration
						.getVatPaymentModel()
						.getVatPaymentLatency(),
				}),
			}),
		];
	}

	_getForecastHorizonAccessor() {
		return `${_(
			"Reference periods: One month",
			"Reference periods: {numberOfReferencePeriods} months",
			this._configuration.getNumberOfReferencePeriods(),
			{
				numberOfReferencePeriods:
					this._configuration.getNumberOfReferencePeriods(),
			},
		)}, ${_(
			"Predicted periods: One month",
			"Predicted periods: {numberOfPredictedPeriods} months",
			this._configuration.getNumberOfPredictedPeriods(),
			{
				numberOfPredictedPeriods:
					this._configuration.getNumberOfPredictedPeriods(),
			},
		)}`;
	}

	_settingRenderer({ name, value = "" }) {
		return (html) =>
			html.div(
				{ class: "forecast-setting", "data-test": "forecast-setting" },
				html.div({ class: "name" }, name),
				html.div(
					{ class: "value", "data-test": "forecast-setting-value" },
					value,
				),
			);
	}

	_specificationRenderer(specification) {
		if (!specification) return (_html) => {};

		return new TextRepresentationAccountSpecificationVisitor().visit(
			specification,
		);
	}
}
